import React from "react";
import { Route, Routes, useParams } from "react-router-dom";

import { CourseCreateProvider } from "./CourseCreateContext";
import { BaseInformationFormSchema, BaseInformationStep } from "./BaseInformation/BaseInformationStep";
import { PaymentScheduleStep } from "./Payment/PaymentScheduleStep";
import { PaymentPolicyStep } from "./PaymentPolicy/PaymentPolicyStep";
import { CourseConfigurationTabs } from "./CourseConfigurationTabs";
import { routes } from "consts";
import { ApplicationFormStep } from "./ApplicationForm/ApplicationFormStep";
import { ScheduleStep } from "./Schedule/ScheduleStep";
import { PaziaBreadcrumbs } from "components/atoms";
import { PaziaLoader } from "components/atoms";
import { SessionsStep } from "./Sessions/SessionsStep";
import { PageMain } from "components/molecules";
import { ExamsStep } from "./Exams/ExamsStep";
import { coursesApi } from "api";
import { getApiErrorMessage, useToaster } from "utils";
import { SurveysStep } from "./Surveys/SurveysStep";
import { useCourseQuery } from "./useCourseQuery";
import { PreviewInformationStep } from "./PreviewInformation/PreviewInformationStep";

export const CourseEditPage = () => {
  const { courseId } = useParams();
  const { showSuccess, showError } = useToaster();

  const { isLoading, course, invalidate } = useCourseQuery(courseId);

  const onSubmit = async (data: BaseInformationFormSchema) => {
    try {
      await coursesApi.update(course!.data.id, data);
      showSuccess("Zaktualizowano kurs.");
    } catch (e) {
      console.error(e);
      showError(getApiErrorMessage(e, "Wystąpił błąd podczas zapisywania zmian."));
    } finally {
      invalidate();
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;
  if (!course) return null;

  return (
    <CourseCreateProvider>
      <PageMain>
        <PaziaBreadcrumbs
          segments={[
            { title: "Grupy kursów", path: routes.COURSE_GROUPS },
            { title: course.group.name, path: `${routes.COURSE_GROUPS_EDIT_COURSES(course.group.id.toString())}` },
            {
              title: [course.data.edition, course.data.variant].filter(Boolean).join(" · "),
              path: routes.COURSES_MANAGE(courseId),
            },
            { title: "Edycja kursu" },
          ]}
        />
        <CourseConfigurationTabs />
        <Routes>
          <Route
            path={routes.COURSES_EDIT_INFO}
            element={<BaseInformationStep course={course} onSubmit={onSubmit} />}
          />
          <Route path={routes.COURSES_EDIT_PREVIEW} element={<PreviewInformationStep course={course} />} />
          <Route path={routes.COURSES_EDIT_PAYMENTS} element={<PaymentScheduleStep course={course} />} />
          <Route path={routes.COURSES_EDIT_PAYMENTS_POLICY} element={<PaymentPolicyStep courseId={course.data.id} />} />
          <Route path={routes.COURSES_EDIT_FORM} element={<ApplicationFormStep courseId={course.data.id} />} />
          <Route path={routes.COURSES_EDIT_SURVEYS} element={<SurveysStep course={course} />} />
          <Route path={routes.COURSES_EDIT_EXAMS} element={<ExamsStep courseId={course.data.id} />} />
          <Route path={routes.COURSES_EDIT_SESSION} element={<SessionsStep />} />
          <Route path={routes.COURSES_EDIT_SCHEDULE} element={<ScheduleStep course={course} />} />
        </Routes>
      </PageMain>
    </CourseCreateProvider>
  );
};
